import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, Music, Sparkles, ChevronLeft, ChevronRight, Repeat } from 'lucide-react';

const MusicCarousel = ({ userMusic = [] }) => {
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLooping, setIsLooping] = useState(false);
  const audioRef = useRef(null);
  const carouselRef = useRef(null);

  // Transform user music data to match carousel format
  const musicTracks = userMusic.map((music, index) => ({
    id: music.id || `music-${index}`,
    title: music.content?.title || `Chanson pour ${music.child_data?.name || 'mon enfant'}`,
    artist: `Z Music • ${music.child_data?.name || 'Custom'}`,
    duration: music.content?.duration ? `${Math.floor(music.content.duration / 60)}:${(music.content.duration % 60).toString().padStart(2, '0')}` : "1:30",
    coverArt: "https://res.cloudinary.com/dogguvwef/image/upload/v1755494850/20250818_1045_Koala_Cosmique_Re%CC%82veur_remix_01k2xa3r0sfxvbjeks6tex4382_hpe0wr.png",
    audioUrl: music.content?.audio_url || "",
    color: "from-blue-400 to-indigo-400",
    childName: music.child_data?.name || 'Child',
    age: music.child_data?.age || 'Unknown',
    style: music.child_data?.musical_style || 'Custom',
    lyrics: music.content?.lyrics || ""
  }));

  const currentMusicTrack = musicTracks[currentTrack];

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current.duration);
      });

      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current.currentTime);
      });

      audioRef.current.addEventListener('ended', () => {
        if (isLooping) {
          audioRef.current.currentTime = 0;
          audioRef.current.play();
        } else {
          nextTrack();
        }
      });
    }
  }, [currentTrack, isLooping]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const nextTrack = () => {
    const next = (currentTrack + 1) % musicTracks.length;
    setCurrentTrack(next);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const prevTrack = () => {
    const prev = currentTrack === 0 ? musicTracks.length - 1 : currentTrack - 1;
    setCurrentTrack(prev);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const newTime = percent * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const scrollToTrack = (index) => {
    setCurrentTrack(index);
    setIsPlaying(false);
    setCurrentTime(0);
    
    if (carouselRef.current) {
      const trackElement = carouselRef.current.children[index];
      if (trackElement) {
        trackElement.scrollIntoView({ behavior: 'smooth', inline: 'center' });
      }
    }
  };

  if (musicTracks.length === 0) {
    return (
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-6 md:p-8 mb-8 border border-blue-100 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
            <Music className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Your Generated Music
          </h3>
          <Sparkles className="w-5 h-5 text-blue-400" />
        </div>

        {/* Empty State */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/50">
          <div className="text-center">
            <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl flex items-center justify-center">
              <Music className="w-10 h-10 md:w-12 md:h-12 text-blue-400" />
            </div>
            <h4 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
              No Music Generated Yet
            </h4>
            <p className="text-gray-500 text-sm md:text-base max-w-md mx-auto leading-relaxed">
              Start creating personalized songs with Z! Your musical creations will appear here once generated.
            </p>
            <div className="mt-6 flex justify-center">
              <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-blue-600 text-sm font-medium">{(navigator.language || 'en').toLowerCase().startsWith('fr') ? 'Prêt pour votre première chanson' : 'Ready for your first song'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-6 md:p-8 mb-8 border border-blue-100 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
          <Music className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          Your Generated Music
        </h3>
        <Sparkles className="w-5 h-5 text-blue-400" />
      </div>

      {/* Current Track Display */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/50">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br ${currentMusicTrack?.color} flex items-center justify-center shadow-lg`}>
            <Music className="w-8 h-8 md:w-10 md:h-10 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-gray-800 text-lg md:text-xl truncate">
              {currentMusicTrack?.title}
            </h4>
            <p className="text-blue-600 text-sm md:text-base">{currentMusicTrack?.artist}</p>
            <p className="text-gray-500 text-xs">
              Age: {currentMusicTrack?.age} • Style: {currentMusicTrack?.style}
            </p>
          </div>
          
          <div className="text-right">
            <p className="text-sm text-gray-500">{currentMusicTrack?.duration}</p>
          </div>
        </div>

        {/* Audio Element */}
        <audio
          ref={audioRef}
          src={currentMusicTrack?.audioUrl}
          preload="metadata"
        />

        {/* Progress Bar */}
        <div className="mt-4">
          <div 
            className="w-full bg-gray-200 rounded-full h-2 cursor-pointer"
            onClick={handleSeek}
          >
            <div 
              className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => setIsLooping(!isLooping)}
            className={`p-2 rounded-xl transition-all duration-200 ${
              isLooping ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <Repeat className="w-5 h-5" />
          </button>

          <button
            onClick={prevTrack}
            disabled={musicTracks.length <= 1}
            className="p-3 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SkipBack className="w-5 h-5" />
          </button>

          <button
            onClick={togglePlay}
            disabled={!currentMusicTrack?.audioUrl}
            className="p-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-white hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          </button>

          <button
            onClick={nextTrack}
            disabled={musicTracks.length <= 1}
            className="p-3 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SkipForward className="w-5 h-5" />
          </button>

          <button className="p-2 rounded-xl text-gray-400 hover:text-gray-600 transition-all duration-200">
            <Volume2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Track List Carousel */}
      {musicTracks.length > 1 && (
        <div className="relative">
          <div
            ref={carouselRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {musicTracks.map((track, index) => (
              <div
                key={track.id}
                onClick={() => scrollToTrack(index)}
                className={`flex-shrink-0 w-64 p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                  index === currentTrack
                    ? 'bg-white shadow-lg scale-105'
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                style={{ scrollSnapAlign: 'center' }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${track.color} flex items-center justify-center`}>
                    <Music className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-semibold text-gray-800 text-sm truncate">{track.title}</h5>
                    <p className="text-blue-600 text-xs truncate">{track.childName}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Scroll Indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {musicTracks.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToTrack(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentTrack ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default MusicCarousel;